import type { Template } from "@scayla/api-client-fetch";
import { template } from "@scayla/api-client-fetch";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { sortTemplateData } from "@/utils/templateUtils";
import { showToastForErrorRef } from "@/utils/toast";

// Cache configuration
const TEMPLATE_CACHE_SIZE_LIMIT = 100;
const TEMPLATE_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

interface TemplateCacheMetadata {
  timestamp: number;
  accessCount: number;
}

export const useTemplateStore = defineStore(
  "templateStore",
  () => {
    // Map-based caching for O(1) performance instead of O(n) array operations
    const templateCache = ref<Map<string, Template>>(new Map());
    const templateCacheMetadata = ref<Map<string, TemplateCacheMetadata>>(new Map());

    // Promise caching to prevent duplicate concurrent requests
    const pendingFetches = ref<Map<string, Promise<Template | undefined>>>(new Map());

    // Computed array view for backwards compatibility
    const templates = computed(() => Array.from(templateCache.value.values()));

    // Check if cached template is still valid (TTL-based)
    function isCacheValid(id: string): boolean {
      const metadata = templateCacheMetadata.value.get(id);
      if (!metadata) return false;
      return Date.now() - metadata.timestamp < TEMPLATE_CACHE_TTL;
    }

    // Update access metadata for LRU cache management
    function updateAccessMetadata(id: string): void {
      const existing = templateCacheMetadata.value.get(id);
      if (existing) {
        existing.accessCount++;
        existing.timestamp = Date.now();
      } else {
        templateCacheMetadata.value.set(id, {
          timestamp: Date.now(),
          accessCount: 1,
        });
      }
    }

    // Manage cache size using LRU eviction
    function manageCacheSize(): void {
      if (templateCache.value.size <= TEMPLATE_CACHE_SIZE_LIMIT) return;

      // Sort by access count and timestamp to find least recently used
      const entries = Array.from(templateCacheMetadata.value.entries()).sort(([, a], [, b]) => {
        // Sort by access count first, then by timestamp
        if (a.accessCount !== b.accessCount) {
          return a.accessCount - b.accessCount;
        }
        return a.timestamp - b.timestamp;
      });

      // Remove oldest entries until we're under the limit
      const toRemove = entries.slice(0, templateCache.value.size - TEMPLATE_CACHE_SIZE_LIMIT);
      for (const [id] of toRemove) {
        templateCache.value.delete(id);
        templateCacheMetadata.value.delete(id);
      }
    }

    interface GetTemplateOptions {
      withoutKindStructure?: boolean;
      forSetting?: boolean;
    }

    function buildCacheKey(id: Template["id"], opts?: GetTemplateOptions): string {
      const light = opts?.withoutKindStructure ? "light" : "full";
      const setting = opts?.forSetting ? "setting" : "module";
      return `${id}:${light}:${setting}`;
    }

    // Optimized getTemplate with intelligent caching
    async function getTemplate(id: Template["id"], opts?: GetTemplateOptions): Promise<Template | undefined> {
      const cacheKey = buildCacheKey(id, opts);

      // 1. Check cache first (O(1) lookup)
      const cached = templateCache.value.get(cacheKey);
      if (cached && isCacheValid(cacheKey)) {
        updateAccessMetadata(cacheKey);
        return cached;
      }

      // 2. Check if already fetching (prevent duplicate requests)
      const existingFetch = pendingFetches.value.get(cacheKey);
      if (existingFetch) {
        return await existingFetch;
      }

      // 3. Fetch and cache the template
      return await fetchTemplate(id, opts);
    }

    // Enhanced fetchTemplate with Promise caching and better error handling
    async function fetchTemplate(id: Template["id"], opts?: GetTemplateOptions): Promise<Template | undefined> {
      const cacheKey = buildCacheKey(id, opts);
      try {
        // Create and cache the fetch promise to prevent duplicates
        const fetchPromise = template({
          query: { templateId: id, forSetting: opts?.forSetting, withoutKindStructure: opts?.withoutKindStructure },
        });
        pendingFetches.value.set(
          cacheKey,
          fetchPromise.then((result) => result.data).catch(() => undefined),
        );

        const result = await fetchPromise;

        if (result.error) {
          showToastForErrorRef(ref(result.error));
          return undefined;
        }

        // Check if template was cached while we were fetching
        const existingCached = templateCache.value.get(cacheKey);
        if (existingCached) {
          return existingCached;
        }

        // Cache the new template
        templateCache.value.set(cacheKey, result.data);
        updateAccessMetadata(cacheKey);
        manageCacheSize();

        return sortTemplateData(result.data);
      } catch (error) {
        console.error("Failed to fetch template:", error);
        return undefined;
      } finally {
        pendingFetches.value.delete(cacheKey);
      }
    }

    // Reset specific template from cache
    function resetTemplate(id: Template["id"]): void {
      for (const key of Array.from(templateCache.value.keys())) {
        if (key.startsWith(`${id}:`)) {
          templateCache.value.delete(key);
          templateCacheMetadata.value.delete(key);
        }
      }
    }

    // Refetch template (reset and fetch)
    async function refetchTemplate(id: Template["id"], opts?: GetTemplateOptions): Promise<Template | undefined> {
      resetTemplate(id);
      return await fetchTemplate(id, opts);
    }

    // Clear entire cache
    function resetStore(): void {
      templateCache.value.clear();
      templateCacheMetadata.value.clear();
      pendingFetches.value.clear();
    }

    // Utility function to get cache statistics (for debugging)
    function getCacheStats() {
      return {
        size: templateCache.value.size,
        limit: TEMPLATE_CACHE_SIZE_LIMIT,
        pendingFetches: pendingFetches.value.size,
      };
    }

    return {
      getTemplate,
      resetTemplate,
      resetStore,
      refetchTemplate,
      templates,
      getCacheStats,
    };
  },
  {},
);
